import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider } from "./context/AppContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetupAccount from "./pages/SetupAccount";
import ExecutiveScorecard from "./pages/ExecutiveScorecard";
import BrokerageDashboard from "./pages/BrokerageDashboard";
import MGADashboard from "./pages/MGADashboard";
import MarketingDashboard from "./pages/MarketingDashboard";
import FinanceDashboard from "./pages/FinanceDashboard";
import PeopleDashboard from "./pages/PeopleDashboard";
import BoardView from "./pages/BoardView";
import UploadCenter from "./pages/UploadCenter";
import KPIDefinitions from "./pages/KPIDefinitions";
import ActionLog from "./pages/ActionLog";
import UserManagement from "./pages/UserManagement";
import ComparePage from "./pages/ComparePage";
import UserTimeline from "./pages/UserTimeline";
import NotificationSettings from "./pages/NotificationSettings";
import SharedView from "./pages/SharedView";
import Profile from "./pages/Profile";
import StrategyDashboard from "./pages/StrategyDashboard";
import SVEPManagement from "./pages/SVEPManagement";
import QuarterlyRocks from "./pages/QuarterlyRocks";
import ProducerPipeline from "./pages/ProducerPipeline";
import BrokerageWeeklyUpload from "./pages/BrokerageWeeklyUpload";
import PlanUpload from "./pages/PlanUpload";

// Components
import PulseGPTButton from "./components/pulse-gpt/PulseGPTButton";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/board-view" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/scorecard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/setup" element={<PublicRoute><SetupAccount /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      
      {/* Shared Dashboard - Public access */}
      <Route path="/shared/:shareId" element={<SharedView />} />

      {/* Protected Routes - All Roles */}
      <Route path="/board-view" element={<ProtectedRoute><BoardView /></ProtectedRoute>} />

      {/* Protected Routes - Admin & ELT Only */}
      <Route 
        path="/scorecard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <ExecutiveScorecard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compare" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <ComparePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/brokerage" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <BrokerageDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/brokerage/pipeline" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <ProducerPipeline />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/brokerage/weekly-upload" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <BrokerageWeeklyUpload />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/plan-upload" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <PlanUpload />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/mga" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <MGADashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/marketing" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <MarketingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <FinanceDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/people" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <PeopleDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <UploadCenter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/kpi-definitions" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <KPIDefinitions />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/action-log" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <ActionLog />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/timeline" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <UserTimeline />
          </ProtectedRoute>
        } 
      />

      {/* Admin Only */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings/notifications" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <NotificationSettings />
          </ProtectedRoute>
        } 
      />

      {/* All Users */}
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />

      {/* Strategy & Execution - Admin/ELT Only */}
      <Route 
        path="/strategy" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <StrategyDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/strategy/svep" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <SVEPManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/strategy/rocks" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'elt']}>
            <QuarterlyRocks />
          </ProtectedRoute>
        } 
      />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/scorecard" replace />} />
      <Route path="*" element={<Navigate to="/scorecard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <AppRoutes />
          <AuthenticatedPulseGPT />
          <Toaster position="top-right" richColors />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Wrapper to only show PulseGPTButton when authenticated
function AuthenticatedPulseGPT() {
  const { user } = useAuth();
  if (!user) return null;
  return <PulseGPTButton />;
}

export default App;
